import { label } from 'cadenza/utils/aria';
import { assert } from 'cadenza/utils/custom-error';
import { uniqueId } from 'cadenza/utils/unique-id';
import { ChangeEvent } from 'cadenza/utils/event-util';
import h from 'hyperscript';
import './select-list.css';
import { isFirefox } from 'cadenza/utils/browser';

export const UNSELECTABLE_OPTION_CLASS = 'unselectable-option';
const COMPONENT_NAME = 'd-select-list';

// the selected/active option is marked with `.is-active`
// to select an option, call the `selection` setter after instantiation
export class SelectList extends HTMLElement {

  #forceSingleSelection;
  #changed = false;

  /**
   * @param {object[]} [options]
   * @param {object} opts
   * @param {string} [opts.ariaLabel]
   * @param {string} [opts.ariaLabelledBy]
   * @param {boolean} [opts.multiple=false]
   * @param {boolean} [opts.forceSingleSelection=false]
   */
  constructor (options = [], {
    ariaLabel,
    ariaLabelledBy,
    multiple = false,
    forceSingleSelection = false
  } = {}) {
    assert(options.length >= 1, 'There must be at least one option');

    super();
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'listbox');
    this.tabIndex = 0;
    label(this, { ariaLabel, ariaLabelledBy });
    this.append(...options.map(option => createOption(option)));

    if (multiple) {
      this.setAttribute('aria-multiselectable', true);
    } else {
      this.selection = this.selection.slice(-1); // select the last selected option
    }

    this.#forceSingleSelection = (!multiple && forceSingleSelection);
    if (this.#forceSingleSelection && this.selection.length === 0) {
      this._toggleSelected(this.children[0], true); // select first option
    }

    this.addEventListener('focus', event => this._onFocus(event));
    this.addEventListener('blur', event => this._onBlur(event));
    this.addEventListener('pointerdown', event => this._onPointerDown(event));
    this.addEventListener('keydown', event => this._onKeyDown(event));
  }

  _onPointerDown (event) {
    const option = event.target.closest('[role=option]');
    if (option && !option.classList.contains(UNSELECTABLE_OPTION_CLASS)) {
      this._toggleSelected(option, undefined, true);
      this._dispatchChangeEvent();
    }
    if (isFirefox) {
      // Without the preventDefault, when something is selected in the data browser flyout,
      // Firefox behaves as if the mouse button is still held down (selects text).
      event.preventDefault();
    }
  }

  _onFocus () {
    if (!this._getActive()) {
      this._setActive(this._selectedOptions[0] || this.children[0]);
    }
  }

  _onBlur () {
    this._dispatchChangeEvent();
  }

  _onKeyDown (event) {
    const key = event.key;
    const previousActive = this._getActive();
    if (key === 'ArrowUp' || key === 'ArrowLeft') {
      event.preventDefault(); // prevent scrolling
      this._setActive(this._getPreviousSelectableSibling(previousActive), true);
    } else if (key === 'ArrowDown' || key === 'ArrowRight') {
      event.preventDefault(); // prevent scrolling
      this._setActive(this._getNextSelectableSibling(previousActive), true);
    } else if (key === 'Home') {
      event.preventDefault(); // prevent scrolling
      this._setActive(this._getFirstSelectableSibling(), true);
    } else if (key === 'End') {
      event.preventDefault(); // prevent scrolling
      this._setActive(this._getLastSelectableSibling(), true);
    } else if (key === ' ') {
      event.preventDefault();
      this._toggleSelected(previousActive, undefined, true);
    } else if (key === 'Enter') {
      event.preventDefault();
      this._dispatchChangeEvent();
    }
  }

  _dispatchChangeEvent () {
    if (this.#changed) {
      this.dispatchEvent(new ChangeEvent());
      this.#changed = false;
    }
  }

  _getActive () {
    const activeId = this.getAttribute('aria-activedescendant');
    return activeId ? this.querySelector(`[id="${activeId}"]`) : null;
  }

  _setActive (option, isUserInteraction) {
    const previousActive = this._getActive();
    if (option && option !== previousActive) {
      if (previousActive) {
        previousActive.classList.remove('is-active');
      }
      this.setAttribute('aria-activedescendant', option.id);
      option.classList.add('is-active');
      if (!this.multiple && isUserInteraction) {
        this._toggleSelected(option, true, isUserInteraction);
      }
    }
  }

  get multiple () {
    return this.getAttribute('aria-multiselectable') === 'true';
  }

  get selection () {
    return this._selectedOptions.map(option => option.dValue ?? option);
  }

  set selection (values) {
    values = new Set(values);
    const multiple = this.multiple;
    this._resetSelection();
    for (const option of this.children) {
      const selected = values.has(option.dValue ?? option);
      option.setAttribute('aria-selected', selected);
      if (selected && !multiple) {
        this._setActive(option);
        return;
      }
    }
  }

  _resetSelection () {
    this.querySelectorAll('[aria-selected=true]')
      .forEach(el => el.removeAttribute('aria-selected'));
  }

  _toggleSelected (option, force, isUserInteraction) {
    if (!this.multiple) {
      this._selectedOptions.forEach(o => {
        if (o !== option) {
          o.setAttribute('aria-selected', false);
        }
      });
    }
    const previousSelected = option.getAttribute('aria-selected') === 'true';
    const selected = (force !== undefined ? force : !previousSelected) || this.#forceSingleSelection;
    option.setAttribute('aria-selected', selected);
    this._setActive(option);
    if (isUserInteraction) {
      this.#changed = (selected !== previousSelected);
    }
  }

  _getNextSelectableSibling (previousActive) {
    const nextSibling = previousActive.nextSibling;
    if (!nextSibling) {
      return previousActive;
    }
    return nextSibling.classList.contains(UNSELECTABLE_OPTION_CLASS)
      ? this._getNextSelectableSibling(nextSibling)
      : nextSibling;
  }

  _getPreviousSelectableSibling (previousActive) {
    const previousSibling = previousActive.previousSibling;
    if (!previousSibling) {
      return previousActive;
    }
    return previousSibling.classList.contains(UNSELECTABLE_OPTION_CLASS)
      ? this._getPreviousSelectableSibling(previousSibling)
      : previousSibling;
  }

  _getFirstSelectableSibling () {
    const firstChild = this.firstElementChild;
    return firstChild.classList.contains(UNSELECTABLE_OPTION_CLASS)
      ? this._getNextSelectableSibling(firstChild)
      : firstChild;
  }

  _getLastSelectableSibling () {
    const lastChild = this.lastElementChild;
    return lastChild.classList.contains(UNSELECTABLE_OPTION_CLASS)
      ? this._getPreviousSelectableSibling(lastChild)
      : lastChild;
  }

  get _selectedOptions () {
    return [ ...this.querySelectorAll('[aria-selected=true]') ];
  }

}

customElements.define(COMPONENT_NAME, SelectList);

function createOption (data) {
  let option = data;
  if (!(option instanceof HTMLElement)) {
    if (typeof data === 'string') {
      data = { value: data, label: data };
    }
    const { value, label, selected = false } = data;
    assert(value, 'Option value is mandatory');
    assert(label, 'Option label is mandatory');
    option = h(`li.${COMPONENT_NAME}--option.ellipsis`, {
      dValue: value, // custom property
      attrs: { 'aria-selected': selected }
    }, label);
  }
  option.id = option.id || uniqueId();
  option.setAttribute('role', 'option');
  return option;
}
